import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useStore, useMe, useAPI } from 'global'
import { toFriendlyDate, toFriendlyTime, camelize, useDeviceStateFor, iconFor } from 'utils'
import { Button, UnhealthyIssue, confirm, closeModal, ApplyDeviceConfigButton } from 'components'
import { DeviceType as DT } from 'enums'
import './styles.scss'

export default function DeviceDetails({ securityDevice: sd }) {
  const { t } = useTranslation(null, { useSuspense: false })
  const { securityDevices, securityDevicesByParent, positionsBySecurityDevice, deleteSecurityDevice: deleteCachedDevice, saveZoneComponent, merchandizeConfig, stayUnlockedConfig } = useStore()
  const { me } = useMe()
  const api = useAPI()
  const securityDevice = securityDevices[sd.id] // Get the data from the context instead, so we can update our state in realtime
  const associatedDevice = securityDevices[securityDevice.parentId] || securityDevicesByParent[securityDevice.id]
  const pos = positionsBySecurityDevice[securityDevice.id]
  const assocPos = positionsBySecurityDevice[associatedDevice?.id]
  const position = pos || assocPos
  const state = useDeviceStateFor()(securityDevice)
  const unhealthyInfo = state.unhealthyReason && <UnhealthyIssue reason={state.unhealthyReason} ports={securityDevice.unhealthyPorts} />

  const sendCommand = command => {
    if (!sd) return
    api.sendCommand(command, sd.id)
      .then(()=>toast.success(t('success.request')))
      .catch(()=>toast.error(t('error.request')))
  }

  const removeFromPosition = () => {
    if (!position) return
    api.saveZoneComponent('position', { ...position, securityDeviceId: null }).then(response => {
      toast.success(t('success.request'))
      const savedResource = response.positions[0]
      saveZoneComponent('position', savedResource)
    }).catch(() => toast.error(t('error.request')))
  }

  const destroy = id => confirm(() => {
    api.deleteSecurityDevice(id).then(()=>{
      toast.success(t('success.request'))
      deleteCachedDevice(id)
      closeModal()
    }).catch(()=>toast.error(t('error.request')))
  })

  const sdTypeStr = t(`device.type.${camelize(securityDevice.deviceType)}`)
  const assocTypeStr = associatedDevice && t(`device.type.${camelize(associatedDevice.deviceType)}`)

  let deleteActions = null
  if (me?.canEditStore) {
    if (position) deleteActions = <p className='subtitle'>{t('device.deletionInstructions')}</p>
    else deleteActions = (
      <>
        <Button danger onClick={()=>destroy(securityDevice.id)}>{t('device.deleteDevice', {type: sdTypeStr})}</Button>
        {associatedDevice && <Button danger onClick={()=>destroy(associatedDevice.id)}>{t('device.deleteDevice', {type: assocTypeStr})}</Button>}
      </>
    )
  }

  const copyDetails = () => {
    let details = securityDevice.serialNumber
    if (associatedDevice) details += `; ${associatedDevice.serialNumber}`
    if (position) details += `; ${position.name}`
    navigator.clipboard.writeText(details)
    toast.success(t('success.copyDeviceDetails'))
  }

  return (
    <div className='device-details-comp col'>
      <div className='row heading'>
        <div className='row'>
          <p className={!position ? 'unassigned' : ''}>{position?.name || t('device.unassigned')}</p>
          {iconFor(state.security)}
          {state.alarming && iconFor(state.alarming)}
        </div>
        {me?.hasOrganizations && <Button className='copy-button' flat icon='content_copy' onClick={copyDetails} />}
      </div>
      <div className='col devices-container'>
        <div className='col device-container'>
          <p className='subtitle'>{t(`device.type.${camelize(securityDevice.deviceType)}`)}</p>
          <p className='serial'>{securityDevice.serialNumber}</p>
        </div>
        {associatedDevice &&
          <div className='col device-container'>
            <p className='subtitle'>{t(`device.type.${camelize(associatedDevice.deviceType)}`)}</p>
            <p className='serial'>{associatedDevice.serialNumber}</p>
          </div>
        }
        {unhealthyInfo}
      </div>
      <div className='col'>
        {securityDevice?.model &&
          <p className='subtitle'>{t('device.model', { model: securityDevice?.model })}</p>
        }
        {securityDevice?.firmwareVersion &&
          <p className='subtitle'>{t('device.firmwareVersion', { version: securityDevice?.firmwareVersion })}</p>
        }
        <p className='subtitle'>{t('device.securityStatusUpdatedAt', { date: toFriendlyDate(securityDevice.securityStatusUpdatedAt), time: toFriendlyTime(securityDevice.securityStatusUpdatedAt) })}</p>
        <p className='subtitle'>{t('device.createdAt', { date: toFriendlyDate(securityDevice.createdAt), time: toFriendlyTime(securityDevice.createdAt) })}</p>
      </div>
      {![DT.lock, DT.repeater].includes(securityDevice.deviceType) &&
        <Button onClick={()=>sendCommand('identify')}>{t('device.identify')}</Button>
      }
      {securityDevice?.deviceType == DT.lock &&
        <>
          <ApplyDeviceConfigButton config={merchandizeConfig} device={sd} commandString={t('commands.merchandize')} />
          <ApplyDeviceConfigButton config={stayUnlockedConfig} device={sd} commandString={t('commands.stayUnlocked')} />
        </>
      }
      {position && me?.canEditStore && <Button onClick={removeFromPosition}>{t('device.removeFromPosition')}</Button>}
      {deleteActions}
    </div>
  )
}
